import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { Button } from "@/components/ui/button";
import { Users, CheckCircle, ArrowRight } from "lucide-react";

const OutsourcingService = () => {
  return (
    <div className="min-h-screen">
      <Navbar />
      <main className="pt-24 pb-16 bg-secondary/30">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <div className="flex items-center gap-3 mb-6">
              <Users className="w-12 h-12 text-accent" />
              <h1 className="text-4xl md:text-5xl font-bold">Outsourcing</h1>
            </div>
            
            <p className="text-xl text-muted-foreground mb-8">
              Flexible security team augmentation. Scale your security capabilities as needed.
            </p>

            <div className="prose prose-lg max-w-none mb-12">
              <h2 className="text-2xl font-bold mb-4">Flexible Security Resources</h2>
              <p className="mb-6">
                Our outsourcing services provide flexible access to experienced security professionals who can augment your existing team or fill critical skill gaps. Scale your security capabilities up or down based on your current needs and projects, with no long-term commitments required.
              </p>

              <h2 className="text-2xl font-bold mb-4">Service Models</h2>
              <div className="space-y-3 mb-6">
                <div className="bg-secondary/20 rounded-lg p-4">
                  <p className="font-semibold mb-2">Fully Managed SOC (24/7/365)</p>
                  <p className="text-sm text-muted-foreground">Round-the-clock security monitoring and incident response for organisations needing continuous protection</p>
                </div>
                <div className="bg-secondary/20 rounded-lg p-4">
                  <p className="font-semibold mb-2">Part-Time Security Analyst (20-40 hours/month)</p>
                  <p className="text-sm text-muted-foreground">Dedicated analyst for ongoing security operations, monitoring, and vulnerability management</p>
                </div>
                <div className="bg-secondary/20 rounded-lg p-4">
                  <p className="font-semibold mb-2">Project-Based Specialists</p>
                  <p className="text-sm text-muted-foreground">Expert consultants for specific initiatives: pen testing, compliance audits, M&A security reviews</p>
                </div>
              </div>

              <h2 className="text-2xl font-bold mb-4">Our Security Experts</h2>
              <div className="bg-accent/10 rounded-lg p-6 mb-6">
                <p className="mb-3">All professionals carry industry-recognised certifications:</p>
                <ul className="space-y-2">
                  <li>• <strong>CISSP, CISM, CEH, OSCP</strong> certified security professionals</li>
                  <li>• <strong>Average 10+ years</strong> hands-on security experience</li>
                  <li>• <strong>Diverse industry backgrounds</strong> including financial services, healthcare, manufacturing</li>
                  <li>• <strong>Continuous training</strong> on latest threats and technologies</li>
                </ul>
              </div>

              <h2 className="text-2xl font-bold mb-4">Response Times</h2>
              <div className="bg-secondary/20 rounded-lg p-6 mb-6">
                <ul className="space-y-2">
                  <li><strong>P1 (Critical) incidents:</strong> 15-minute response time</li>
                  <li><strong>P2 (High) incidents:</strong> 1-hour response time</li>
                  <li><strong>P3 (Medium) incidents:</strong> 4-hour response time</li>
                  <li><strong>P4 (Low) incidents:</strong> Next business day</li>
                </ul>
              </div>

              <h2 className="text-2xl font-bold mb-4">Services We Provide</h2>
              <ul className="space-y-3 mb-6">
                <li className="flex items-start gap-2">
                  <CheckCircle className="w-5 h-5 text-accent mt-1 flex-shrink-0" />
                  <span><strong>Security operations center (SOC) support:</strong> 24/7 monitoring, alert triage, and incident escalation</span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle className="w-5 h-5 text-accent mt-1 flex-shrink-0" />
                  <span><strong>Incident response and handling:</strong> Rapid containment, forensics, and recovery support</span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle className="w-5 h-5 text-accent mt-1 flex-shrink-0" />
                  <span><strong>Vulnerability management:</strong> Continuous scanning, prioritization, and patch management</span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle className="w-5 h-5 text-accent mt-1 flex-shrink-0" />
                  <span><strong>Penetration testing:</strong> Scheduled and on-demand testing to identify exploitable weaknesses</span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle className="w-5 h-5 text-accent mt-1 flex-shrink-0" />
                  <span><strong>Security project management:</strong> Leading security initiatives from planning to completion</span>
                </li>
              </ul>

              <h2 className="text-2xl font-bold mb-4">Client Examples</h2>
              <div className="space-y-3">
                <div className="border-l-4 border-accent pl-4">
                  <p className="font-semibold">50-Employee Fintech Startup</p>
                  <p className="text-sm text-muted-foreground">Providing 24/7 SOC monitoring and quarterly penetration testing to meet investor security requirements</p>
                </div>
                <div className="border-l-4 border-accent pl-4">
                  <p className="font-semibold">Manufacturing Company - 6-Month Transformation</p>
                  <p className="text-sm text-muted-foreground">Interim CISO guiding OT/IT security convergence during permanent hire search</p>
                </div>
                <div className="border-l-4 border-accent pl-4">
                  <p className="font-semibold">Professional Services Firm - Project Based</p>
                  <p className="text-sm text-muted-foreground">M&A security due diligence for three acquisitions over 18 months</p>
                </div>
              </div>
            </div>

            <div className="bg-secondary/30 rounded-lg p-8">
              <h3 className="text-2xl font-bold mb-4">Need to augment your security team?</h3>
              <p className="text-muted-foreground mb-6">
                Let's discuss how our outsourcing services can provide the security expertise and capacity you need.
              </p>
              <a 
                href="https://outlook.office365.com/owa/calendar/HazelMcPherson4FOXSecurity@4fox.uk/bookings/" 
                target="_blank" 
                rel="noopener noreferrer"
              >
                <Button size="lg" className="bg-accent hover:bg-accent/90">
                  Book a Consultation
                  <ArrowRight className="ml-2 w-5 h-5" />
                </Button>
              </a>
            </div>
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
};

export default OutsourcingService;
